/**
 * Máquinas de estado explícitas.
 *
 * No legado as transições eram `if` espalhados por controllers, o que produziu
 * estados impossíveis: NC encerrada sem CAPA (C10), documento obsoleto rotulado
 * "Vencido" (BUG-12), status 'V' que ninguém gravava (ged.md §3.1).
 *
 * Aqui a transição é um dado, não código. Isso é também o que torna a
 * rastreabilidade requisito→teste viável: cada aresta vira um caso de OQ.
 */
import { DocumentVersionStatus, NonconformityStatus } from '@qualicloud/contracts';
export class IllegalTransitionError extends Error {
    constructor(machine, from, to) {
        super(`Transição inválida em ${machine}: ${from} → ${to}.`);
        this.machine = machine;
        this.from = from;
        this.to = to;
        this.name = 'IllegalTransitionError';
    }
}
export class StateMachine {
    constructor(name, transitions) {
        this.name = name;
        this.transitions = transitions;
        this.index = new Map();
        for (const t of transitions) {
            this.index.set(`${t.from}::${t.event}`, t);
        }
    }
    can(from, event) {
        return this.index.has(`${from}::${event}`);
    }
    transition(from, event) {
        const t = this.index.get(`${from}::${event}`);
        if (!t) {
            throw new IllegalTransitionError(this.name, from, `(evento ${event})`);
        }
        return t;
    }
    next(from, event) {
        return this.transition(from, event).to;
    }
    eventsFrom(from) {
        return this.transitions.filter((t) => t.from === from);
    }
    /** Arestas que exigem assinatura — insumo direto da matriz de rastreabilidade. */
    signedTransitions() {
        return this.transitions.filter((t) => !!t.requiresSignatureAct);
    }
}
// ─────────────────────────────────────────────────────────────────────────────
// Versão de documento — plano §7.2
// ─────────────────────────────────────────────────────────────────────────────
export const DocumentVersionEvent = {
    SUBMIT: 'SUBMIT',
    APPROVE: 'APPROVE',
    REJECT: 'REJECT',
    AUTO_APPROVE: 'AUTO_APPROVE',
    SUPERSEDE: 'SUPERSEDE',
    EXPIRE: 'EXPIRE',
    OBSOLETE: 'OBSOLETE',
    RESUBMIT: 'RESUBMIT',
};
export const documentVersionMachine = new StateMachine('DocumentVersion', [
    {
        from: DocumentVersionStatus.DRAFT,
        to: DocumentVersionStatus.IN_REVIEW,
        event: DocumentVersionEvent.SUBMIT,
        description: 'Autor submete a versão para aprovação.',
    },
    {
        from: DocumentVersionStatus.DRAFT,
        to: DocumentVersionStatus.APPROVED,
        event: DocumentVersionEvent.AUTO_APPROVE,
        requiresSignatureAct: 'DOCUMENT_APPROVE',
        description: 'Aprovação automática (legado: controle="on"). Exige document:approve:all ' +
            'e ainda assim é assinada — o legado promovia sem assinatura nenhuma.',
    },
    {
        from: DocumentVersionStatus.IN_REVIEW,
        to: DocumentVersionStatus.APPROVED,
        event: DocumentVersionEvent.APPROVE,
        requiresSignatureAct: 'DOCUMENT_APPROVE',
        description: 'Critério da política de aprovação satisfeito.',
    },
    {
        from: DocumentVersionStatus.IN_REVIEW,
        to: DocumentVersionStatus.REJECTED,
        event: DocumentVersionEvent.REJECT,
        requiresSignatureAct: 'DOCUMENT_REJECT',
        description: 'Ao menos um aprovador reprovou. Justificativa obrigatória.',
    },
    {
        from: DocumentVersionStatus.REJECTED,
        to: DocumentVersionStatus.SUPERSEDED,
        event: DocumentVersionEvent.SUPERSEDE,
        description: 'Nova versão emitida sobre uma versão reprovada.',
    },
    {
        from: DocumentVersionStatus.APPROVED,
        to: DocumentVersionStatus.SUPERSEDED,
        event: DocumentVersionEvent.SUPERSEDE,
        description: 'Nova versão aprovada substitui esta. Legado: ies_versao 1 → 0.',
    },
    {
        from: DocumentVersionStatus.IN_REVIEW,
        to: DocumentVersionStatus.SUPERSEDED,
        event: DocumentVersionEvent.SUPERSEDE,
        description: 'Nova versão emitida antes de a anterior ser decidida.',
    },
    {
        from: DocumentVersionStatus.APPROVED,
        to: DocumentVersionStatus.EXPIRED,
        event: DocumentVersionEvent.EXPIRE,
        description: 'Validade vencida. MATERIALIZADO por job noturno — no legado era derivado ' +
            'em cada query, com 3 predicados divergentes.',
    },
    {
        from: DocumentVersionStatus.EXPIRED,
        to: DocumentVersionStatus.SUPERSEDED,
        event: DocumentVersionEvent.SUPERSEDE,
        description: 'Nova versão substitui a versão vencida.',
    },
    {
        from: DocumentVersionStatus.APPROVED,
        to: DocumentVersionStatus.OBSOLETE,
        event: DocumentVersionEvent.OBSOLETE,
        requiresSignatureAct: 'DOCUMENT_OBSOLETE',
        description: 'Obsolescência deliberada. ISO 13485 §4.2.4(f). Não incrementa a versão ' +
            '— regra do legado preservada.',
    },
    {
        from: DocumentVersionStatus.EXPIRED,
        to: DocumentVersionStatus.OBSOLETE,
        event: DocumentVersionEvent.OBSOLETE,
        requiresSignatureAct: 'DOCUMENT_OBSOLETE',
        description: 'Obsoletar um documento já vencido.',
    },
    {
        from: DocumentVersionStatus.OBSOLETE,
        to: DocumentVersionStatus.SUPERSEDED,
        event: DocumentVersionEvent.SUPERSEDE,
        description: 'Reativação por nova versão: emitir uma versão nova sobre um documento ' +
            'obsoleto rebaixa a versão obsoleta a superada. Sem esta aresta o documento ' +
            'ficaria permanentemente travado, e ISO 13485 §4.2.4(b) exige que documentos ' +
            'possam ser revisados e reaprovados. No legado a obsolescência também não era ' +
            'terminal: addHistory() rebaixava a vigente e criava outra, então nada impedia ' +
            'uma nova versão depois (ged.md §6.6).',
    },
]);
/** Estados em que o binário pode ser baixado/impresso sem restrição adicional. */
export const DOWNLOADABLE_STATUSES = [
    DocumentVersionStatus.APPROVED,
];
export function isDownloadableStatus(status) {
    return DOWNLOADABLE_STATUSES.includes(status);
}
/**
 * Veredito do porteiro de saída de arquivo — download e impressão.
 *
 * `DENIED_NO_FLAG` e `BLOCKED_OBSOLETE` são recusa; os demais liberam.
 */
export const FileRelease = {
    ALLOWED: 'ALLOWED',
    /** Não é versão vigente: só sai para quem tem `document:read:all`. */
    DENIED_NOT_CURRENT: 'DENIED_NOT_CURRENT',
    /** Sem a flag DOWNLOAD efetiva sobre o documento. */
    DENIED_NO_FLAG: 'DENIED_NO_FLAG',
    /** ISO 13485 §4.2.4(f) — obsoleto não sai como binário utilizável. */
    BLOCKED_OBSOLETE: 'BLOCKED_OBSOLETE',
};
/**
 * Porteiro único de saída de arquivo, usado pelo download E pela impressão.
 *
 * PARIDADE ged.md §15.15 (`printDocument()`, `ged.php:1266-1282`): o legado
 * derivava a permissão de IMPRESSÃO da coluna `download` de
 * `erp_document_permission` — não havia flag própria de impressão. Preservado:
 * `hasDownloadFlag` governa os dois caminhos. O que o legado fazia errado
 * (BUG-06) era consultar **apenas o escopo usuário**, negando quem tinha
 * `download = 1` por departamento ou empresa; aqui a flag já chega resolvida
 * pelos três escopos.
 *
 * `strictStatusGate` separa os dois usos:
 *   · `false` (download) — PARIDADE ged.md §9.5 / BUG-83: no legado o
 *     vencimento não bloqueava download nem visualização. Só a obsolescência
 *     bloqueia, por ISO 13485 §4.2.4(f).
 *   · `true` (impressão) — uma cópia impressa sai do sistema e passa a
 *     circular sem controle de versão. ISO 13485 §4.2.4(d) exige que a versão
 *     pertinente esteja disponível nos pontos de uso: imprimir algo que não é a
 *     versão vigente exige autoridade administrativa (`document:read:all`), e a
 *     cópia sai carimbada.
 */
export function resolveFileRelease(params) {
    if (!params.hasDownloadFlag)
        return FileRelease.DENIED_NO_FLAG;
    if (params.status === DocumentVersionStatus.OBSOLETE)
        return FileRelease.BLOCKED_OBSOLETE;
    if (!params.strictStatusGate)
        return FileRelease.ALLOWED;
    if (isDownloadableStatus(params.status))
        return FileRelease.ALLOWED;
    return params.hasReadAllPermission ? FileRelease.ALLOWED : FileRelease.DENIED_NOT_CURRENT;
}
/**
 * ISO 13485 §4.2.4(f) — impedir uso não intencional de documento obsoleto.
 * Divergência D-VAL-05: o legado não bloqueava nada.
 */
export function requiresWatermarkOnExport(status) {
    return (status === DocumentVersionStatus.OBSOLETE ||
        status === DocumentVersionStatus.EXPIRED ||
        status === DocumentVersionStatus.SUPERSEDED ||
        status === DocumentVersionStatus.REJECTED ||
        status === DocumentVersionStatus.DRAFT ||
        status === DocumentVersionStatus.IN_REVIEW);
}
export const WATERMARK_TEXT = {
    [DocumentVersionStatus.OBSOLETE]: 'OBSOLETO — NÃO USAR',
    [DocumentVersionStatus.EXPIRED]: 'VENCIDO — NÃO USAR',
    [DocumentVersionStatus.SUPERSEDED]: 'VERSÃO SUPERADA — NÃO USAR',
    [DocumentVersionStatus.REJECTED]: 'REPROVADO — NÃO USAR',
    [DocumentVersionStatus.DRAFT]: 'RASCUNHO — NÃO CONTROLADO',
    [DocumentVersionStatus.IN_REVIEW]: 'EM APROVAÇÃO — NÃO CONTROLADO',
};
// ─────────────────────────────────────────────────────────────────────────────
// Não-conformidade — corrige C10 (plano §7.3)
//
// CONECTADO NA ONDA 2. `nonconformityMachine` é percorrida exclusivamente por
// `ms-core/src/modules/nonconformity/nonconformity.service.ts`, e
// `assertCapaIsComplete` é chamada dentro da transação assinada de
// `NC_CLOSE` — é INVARIANTE do encerramento, não `if` de controller.
//
// Este é o achado C10 do plano, e a diferença em relação ao legado é o ponto
// inteiro da reescrita: lá `finalizarNc()` (`controller/reuniao/tarefas.php:547`)
// implementava a regra C16 e **não tinha nenhum chamador** (BUG-NC-04). A UI
// encerrava a NC por `managerEval`/`saveNaoProcede`, que gravavam
// `status_nc = 'F'` sem olhar tarefa alguma — NC aparecia encerrada sem CAPA.
// Aqui não existe aresta para CLOSED que não passe por
// PENDING_EFFECTIVENESS (ou NOT_APPLICABLE), e a que passa exige o gate.
// ─────────────────────────────────────────────────────────────────────────────
export const NonconformityEvent = {
    START_EVALUATION: 'START_EVALUATION',
    ACCEPT: 'ACCEPT',
    REJECT_AS_NOT_APPLICABLE: 'REJECT_AS_NOT_APPLICABLE',
    START_TREATMENT: 'START_TREATMENT',
    SUBMIT_FOR_EFFECTIVENESS: 'SUBMIT_FOR_EFFECTIVENESS',
    EFFECTIVENESS_FAILED: 'EFFECTIVENESS_FAILED',
    CLOSE: 'CLOSE',
};
export const nonconformityMachine = new StateMachine('Nonconformity', [
    {
        from: NonconformityStatus.OPEN,
        to: NonconformityStatus.UNDER_EVALUATION,
        event: NonconformityEvent.START_EVALUATION,
        description: 'Gestor assume a avaliação de procedência.',
    },
    {
        from: NonconformityStatus.UNDER_EVALUATION,
        to: NonconformityStatus.NOT_APPLICABLE,
        event: NonconformityEvent.REJECT_AS_NOT_APPLICABLE,
        requiresSignatureAct: 'NC_NOT_APPLICABLE',
        description: 'Gestor registra "não procede". Justificativa obrigatória.',
    },
    {
        from: NonconformityStatus.UNDER_EVALUATION,
        to: NonconformityStatus.ACCEPTED,
        event: NonconformityEvent.ACCEPT,
        requiresSignatureAct: 'NC_EVALUATE',
        description: 'Procedência aceita, com re-avaliação da matriz GUT.',
    },
    {
        from: NonconformityStatus.ACCEPTED,
        to: NonconformityStatus.IN_TREATMENT,
        event: NonconformityEvent.START_TREATMENT,
        description: 'Plano de ação 5W2H criado a partir das causas-raiz.',
    },
    {
        from: NonconformityStatus.IN_TREATMENT,
        to: NonconformityStatus.PENDING_EFFECTIVENESS,
        event: NonconformityEvent.SUBMIT_FOR_EFFECTIVENESS,
        description: 'Todas as ações executadas; aguarda verificação de eficácia.',
    },
    {
        from: NonconformityStatus.PENDING_EFFECTIVENESS,
        to: NonconformityStatus.IN_TREATMENT,
        event: NonconformityEvent.EFFECTIVENESS_FAILED,
        requiresSignatureAct: 'EFFECTIVENESS_REJECT',
        description: 'Eficácia reprovada: reabre o tratamento e gera SACAP de follow-up (+3 dias, ' +
            'herdando participantes) — regra do legado preservada.',
    },
    {
        from: NonconformityStatus.PENDING_EFFECTIVENESS,
        to: NonconformityStatus.CLOSED,
        event: NonconformityEvent.CLOSE,
        requiresSignatureAct: 'NC_CLOSE',
        description: 'Encerramento. INVARIANTE: toda ação tem data de fim e verificação de ' +
            'eficácia aprovada. No legado isso era um if de controller que nunca era chamado (BUG-NC-04).',
    },
    {
        from: NonconformityStatus.NOT_APPLICABLE,
        to: NonconformityStatus.CLOSED,
        event: NonconformityEvent.CLOSE,
        requiresSignatureAct: 'NC_CLOSE',
        description: 'Encerramento de NC julgada improcedente.',
    },
]);
export class IncompleteCapaError extends Error {
    constructor(pendingActionIds, failedActionIds) {
        const parts = [];
        if (pendingActionIds.length) {
            parts.push(`${pendingActionIds.length} ação(ões) sem conclusão`);
        }
        if (failedActionIds.length) {
            parts.push(`${failedActionIds.length} ação(ões) com eficácia reprovada`);
        }
        super(`A não-conformidade não pode ser encerrada: ${parts.join(' e ')}. ` +
            'ISO 13485 §8.5.2 exige verificação da eficácia da ação corretiva.');
        this.pendingActionIds = pendingActionIds;
        this.failedActionIds = failedActionIds;
        this.name = 'IncompleteCapaError';
    }
}
export function assertCapaIsComplete(actions) {
    const pending = actions
        .filter((a) => a.completedAt === null || a.effectivenessApproved === null)
        .map((a) => a.id);
    const failed = actions.filter((a) => a.effectivenessApproved === false).map((a) => a.id);
    if (pending.length || failed.length) {
        throw new IncompleteCapaError(pending, failed);
    }
}
//# sourceMappingURL=state-machine.js.map